import React, { useEffect, useState, FunctionComponent, ReactElement, SyntheticEvent } from 'react';

import { BFLoader } from '@components/common/loader/main';
import { CustomFieldValuesListResponse, FlattenedCustomFieldKeyValuesMap } from '@api/v4/assets/customFieldTypes';
import { useFetch } from '@api/ApiHelper';
import { flattenCustomFieldKeyValuesList } from '@api/v4/assets/helpers';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';

interface CustomFieldsAccordionProps {
  assetKey: string;
  closeModal: () => void | undefined;
  count: number;
}

const valuesAsString = (values: { value: string }[]): string => values.map((valueObject) => (
  valueObject.value
)).join(', ');

export const CustomFieldsAccordion: FunctionComponent<CustomFieldsAccordionProps> = ({
  assetKey,
  closeModal,
  count,
}) => {
  const url = `/api/v4/assets/${assetKey}/custom_field_values`;
  const { loading, response } = useFetch<CustomFieldValuesListResponse>({
    include: 'custom_field_key',
    params: { digest: BFG.manifest?.digest, ugt_locale: determineUGTLocale() },
    url,
  });
  const [showMore, setShowMore] = useState(false);
  const [customFieldKeyValuesList, setCustomFieldKeyValuesList] = useState<FlattenedCustomFieldKeyValuesMap>({});

  const filterOnCustomFields = (e: SyntheticEvent, key: string, value: string): void => {
    e.preventDefault();
    e.stopPropagation();

    if (closeModal) {
      closeModal(); // method to close modal in org search context
    } else {
      BF.dialog.dismiss(); // method to close modal in all other contexts
    }

    const escapedValue = value.replace(/"/g, '\\"');
    const query = escapedValue.includes(',')
      ? `custom_fields.${key}.moderate:"(${escapedValue.replace(',', ' OR')})"`
      : `custom_fields.${key}:"${escapedValue}"`;
    BF.fx.fillInAndFilterQuery(query);
  };

  const renderRow = ({ customFieldKey, customFieldValues }): ReactElement => (
    <tr
      key={customFieldKey.id}
      className="s-custom-field"
      onClick={!BFG.showPageLite ? (e): void => filterOnCustomFields(e, customFieldKey.name, valuesAsString(customFieldValues)) : undefined}
    >
      <td className="col-xs-4">{customFieldKey.name}</td>
      <td className="col-xs-8">
        {valuesAsString(customFieldValues)}
      </td>
    </tr>
  );

  useEffect((): void => {
    if (response) {
      setCustomFieldKeyValuesList(flattenCustomFieldKeyValuesList(response));
    }
  }, [response]);

  if (loading) {
    return <BFLoader />;
  }

  if (!Object.keys(customFieldKeyValuesList).length) {
    return null;
  }

  return (
    <>
      <table className="table s-custom-fields-section">
        <tbody>
          {Object.keys(customFieldKeyValuesList).slice(0, showMore ? undefined : count).map((key) => (
            renderRow(customFieldKeyValuesList[key])
          ))}
          {Object.keys(customFieldKeyValuesList).length > count && (
            <tr
              className="s-hidden-fields"
              onClick={(): void => setShowMore((prevState) => !prevState)}
            >
              <td colSpan={2}>
                {showMore ? bfTranslate("Hide Some Fields") : bfTranslate("Show All Fields")}
                <span
                  aria-hidden="true"
                  className={showMore ? 'bff-close' : 'bff-caret-down'}
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
};
